/**
 * Session 3C — shared API client for the 27 bespoke policy pages.
 *
 * Per §0.1 of the prompt: frontend + backend ship in the SAME PR per
 * screen. This file is updated alongside each screen's PR.
 *
 * Per §3.2 of the prompt: every numeric save on every policy screen
 * routes through `policy-save.ts::savePolicyValue()` which wraps this
 * client + the Mutaba'aa veto check.
 */

import { apiBaseUrl, ApiError, authHeaders } from "./api";

async function _safeGet<T>(path: string, fallback: T): Promise<T> {
  let headers: Record<string, string>;
  try {
    headers = await authHeaders();
  } catch {
    headers = {};
  }
  headers["Content-Type"] = "application/json";
  try {
    const r = await fetch(`${apiBaseUrl}${path}`, { headers });
    if (!r.ok) {
      if (r.status === 404 || r.status === 501) return fallback;
      let detail = r.statusText;
      try {
        const body = await r.json();
        if (body && typeof body.detail === "string") detail = body.detail;
      } catch {
        // non-JSON
      }
      throw new ApiError(r.status, detail);
    }
    return (await r.json()) as T;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

/** A single numeric policy value with its 3-layer override stack. */
export interface PolicyValueWithStack {
  /** The active resolved value (rendered to the user). */
  value: number;
  /** The locked KSA statutory floor — null if no statute applies. */
  ksa_floor: number | null;
  /** Article citation for the floor — null if no statute applies. */
  ksa_article: string | null;
  /** The CustomerGroup default — null if not set. */
  group_default: number | null;
  /** The LegalEntity override — null if not set. */
  le_override: number | null;
  /** Which layer is currently active: 'floor' | 'group' | 'le'. */
  active_layer: "floor" | "group" | "le";
  /** Last-changed audit metadata. */
  last_changed_actor: string | null;
  last_changed_at: string | null;
  last_change_note: string | null;
}

/** Mutaba'aa statutory-floor assessment for a single attempted save. */
export interface MutabaaFloorAssessment {
  blocked: boolean;
  article_citation: string | null;
  article_summary: string | null;
  attempted_value: number | null;
  floor_value: number | null;
  /** When non-null + non-blocked, render as warning not veto. */
  warning_message: string | null;
}

/** Common policy row shape across most screens. */
export interface PolicyRow {
  id: string;
  scope_label: string;
  active: boolean;
  effective_from: string;
  override_source: "floor" | "group" | "le";
}

// ---------------------------------------------------------------------------
// Per-domain item shapes (one per screen; spec'd by §4)
// ---------------------------------------------------------------------------

export interface AccrualRuleItem extends PolicyRow {
  leave_type_code: string;
  leave_type_name_en: string;
  calc_method: "fixed_annual" | "monthly_accrual" | "service_tiered" | "once_per_lifetime" | "as_needed" | "unlimited";
  rate: PolicyValueWithStack;
  floor_status: "above" | "at_floor" | "below";
}

export interface EligibilityCellItem {
  leave_type_code: string;
  leave_type_name_en: string;
  grade_code: string;
  grade_name_en: string;
  eligible: boolean;
  min_tenure_months: number | null;
  max_days_per_year_override: number | null;
  source_layer: "default" | "le";
}

export interface EncashmentRuleItem extends PolicyRow {
  leave_type_code: string;
  leave_type_name_en: string;
  encashable_on_eos: boolean;
  max_days_encashable: PolicyValueWithStack;
  valuation_method: "basic" | "basic_plus_allowances" | "total_wage";
  article_109_compliant: boolean;
}

export interface CarryForwardRuleItem extends PolicyRow {
  leave_type_code: string;
  leave_type_name_en: string;
  carry_forward_cap_days: PolicyValueWithStack;
  expiry_months: number | null;
  negative_balance_allowed: boolean;
}

export interface LeaveYearPeriodItem {
  id: string;
  period_type: "calendar_year" | "fiscal_year" | "hijri_year" | "custom";
  start_date: string;
  end_date: string;
  active_from: string;
  notes: string | null;
}

export interface LoanEligibilityRuleItem extends PolicyRow {
  product_code: string;
  product_name_en: string;
  min_tenure_months: PolicyValueWithStack;
  min_grade_code: string | null;
  max_concurrent_loans: PolicyValueWithStack;
  min_service_years: number;
}

export interface Article90ConfigItem {
  cap_pct: PolicyValueWithStack;
  aggregation_loans: boolean;
  aggregation_garnishments: boolean;
  aggregation_unpaid_leave: boolean;
  aggregation_advances: boolean;
  grace_days: number;
  warning_threshold_pct: number;
}

export interface SettlementMethodItem extends PolicyRow {
  product_code: string;
  product_name_en: string;
  early_payment_discount_pct: number;
  arrears_escalation_threshold: number;
  auto_writeoff_threshold: number | null;
}

export interface ShiftPatternItem extends PolicyRow {
  pattern_name: string;
  cycle_description: string;
  default_assignments_count: number;
  employees_on_pattern: number;
}

export interface OvertimeRuleItem extends PolicyRow {
  rule_type: "weekday_ot" | "weekend_ot" | "public_holiday_ot";
  multiplier: PolicyValueWithStack;
  statutory_floor: number;
}

export interface GraceRuleItem extends PolicyRow {
  scope_kind: "global" | "shift" | "grade";
  scope_label_specific: string;
  grace_minutes: PolicyValueWithStack;
  escalation_after_per_month: number;
}

export interface BiometricDevicePolicyItem extends PolicyRow {
  device_name: string;
  location_label: string;
  ip_serial: string;
  sync_schedule_cron: string;
  last_sync_at: string | null;
  status: "active" | "maintenance" | "retired";
}

export interface GeofenceItem extends PolicyRow {
  zone_name: string;
  zone_type: "office" | "branch" | "wfh_approved";
  latitude: number;
  longitude: number;
  radius_m: number;
}

export interface EarningCodeItem extends PolicyRow {
  code: string;
  name_en: string;
  category: string;
  calc_behavior: "fixed" | "percent" | "per_day" | "formula";
  taxable: boolean;
  gosi_inclusive: boolean;
  eos_inclusive: boolean;
}

export interface DeductionCodeItem extends PolicyRow {
  code: string;
  name_en: string;
  category: string;
  article_90_cap_counted: boolean;
  is_mandatory: boolean;
}

export interface PayrollCalendarItem extends PolicyRow {
  period_type: "monthly" | "biweekly" | "weekly";
  cutoff_date: string;
  pay_date: string;
  lock_lead_time_days: number;
}

export interface GosiRateItem {
  employee_category: "saudi" | "non_saudi";
  contribution_type: string;
  employee_rate_pct: number;
  employer_rate_pct: number;
  effective_from: string;
  source: string;
}

export interface GlMappingItem extends PolicyRow {
  payroll_component: string;
  gl_account: string;
  gl_account_name: string;
  system: "d365" | "sap" | "quickbooks" | "generic";
}

export interface BankTemplateItem extends PolicyRow {
  bank_code: "riyad" | "alrajhi" | "snb" | "anb" | "albilad" | "other";
  bank_name_en: string;
  sif_template_version: string;
  is_primary: boolean;
  last_used_at: string | null;
}

export interface TaxWithholdingItem extends PolicyRow {
  jurisdiction: string;
  tax_type: string;
  rate_pct: number;
  threshold_amount: number | null;
  source_kind: "statutory" | "customer_override";
}

export interface TravelCategoryItem extends PolicyRow {
  category_name: string;
  description: string;
  eligible_grades: string[];
  booking_class_default: "economy" | "business" | "first";
  per_diem_tier: string;
}

export interface DestinationItem extends PolicyRow {
  destination_code: string;
  destination_name_en: string;
  country: string;
  category_tier: string;
}

export interface AdvanceReconciliationItem {
  advance_pct: PolicyValueWithStack;
  reconciliation_deadline_days: number;
  auto_deduct_if_overdue: boolean;
}

export interface BookingClassRuleItem extends PolicyRow {
  grade_code: string;
  flight_class: "economy" | "business" | "first";
  hotel_star_cap: number;
  ground_transport_allowed: string[];
  daily_limit_sar: number;
}

export interface EosCalcRuleItem extends PolicyRow {
  service_tier: "year_1_5" | "year_5_10" | "year_10_plus";
  base_multiplier: PolicyValueWithStack;
  enhancement_pct: number;
}

export interface TerminationReasonItem extends PolicyRow {
  reason_code: string;
  article_reference: string;
  affects_eos: boolean;
  affects_notice_period: boolean;
  article_80_absconding: boolean;
}

export interface NoticePeriodRuleItem extends PolicyRow {
  grade_code: string;
  notice_days: PolicyValueWithStack;
  pay_in_lieu_allowed: boolean;
  waivable_conditions: string[];
}

// ---------------------------------------------------------------------------
// Shared brief + suggestion helpers
// ---------------------------------------------------------------------------

export interface PolicyBriefCounts {
  total: number;
  above_floor: number;
  at_floor: number;
  below_floor: number;
  last_change_actor: string | null;
  last_change_days_ago: number | null;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

function _listEndpoint<T>(path: string, leId: string): Promise<{ items: T[]; counts?: PolicyBriefCounts }> {
  return _safeGet<{ items: T[]; counts?: PolicyBriefCounts }>(
    `${path}?legal_entity_id=${encodeURIComponent(leId)}`,
    { items: [] },
  );
}

export const session3cApi = {
  // Leaves Policies
  listAccrualRules: (leId: string) =>
    _listEndpoint<AccrualRuleItem>("/api/policies/leave/accrual-rules", leId),
  listEligibilityMatrix: (leId: string) =>
    _listEndpoint<EligibilityCellItem>("/api/policies/leave/eligibility-matrix", leId),
  listEncashmentRules: (leId: string) =>
    _listEndpoint<EncashmentRuleItem>("/api/policies/leave/encashment-rules", leId),
  listCarryForwardRules: (leId: string) =>
    _listEndpoint<CarryForwardRuleItem>("/api/policies/leave/carry-forward-rules", leId),
  listLeaveYearPeriods: (leId: string) =>
    _listEndpoint<LeaveYearPeriodItem>("/api/policies/leave/year-calendar", leId),
  // Loans Policies
  listLoanEligibilityRules: (leId: string) =>
    _listEndpoint<LoanEligibilityRuleItem>("/api/policies/loans/eligibility-rules", leId),
  getArticle90Config: (leId: string) =>
    _safeGet<Article90ConfigItem | { items: [] }>(
      `/api/policies/loans/article-90-config?legal_entity_id=${encodeURIComponent(leId)}`,
      { items: [] } as { items: [] },
    ),
  listSettlementMethods: (leId: string) =>
    _listEndpoint<SettlementMethodItem>("/api/policies/loans/settlement-methods", leId),
  // Attendance & Shifts Policies
  listShiftPatterns: (leId: string) =>
    _listEndpoint<ShiftPatternItem>("/api/policies/shifts/patterns", leId),
  listOvertimeRules: (leId: string) =>
    _listEndpoint<OvertimeRuleItem>("/api/policies/shifts/overtime-rules", leId),
  listGraceRules: (leId: string) =>
    _listEndpoint<GraceRuleItem>("/api/policies/shifts/grace-rules", leId),
  listBiometricDevicesPolicy: (leId: string) =>
    _listEndpoint<BiometricDevicePolicyItem>("/api/policies/shifts/biometric-devices", leId),
  listGeofences: (leId: string) =>
    _listEndpoint<GeofenceItem>("/api/policies/shifts/geofences", leId),
  // Payroll Policies
  listEarnings: (leId: string) =>
    _listEndpoint<EarningCodeItem>("/api/policies/payroll/earnings-catalog", leId),
  listDeductions: (leId: string) =>
    _listEndpoint<DeductionCodeItem>("/api/policies/payroll/deductions-catalog", leId),
  listPayrollCalendars: (leId: string) =>
    _listEndpoint<PayrollCalendarItem>("/api/policies/payroll/calendars", leId),
  listGosiRates: (leId: string) =>
    _listEndpoint<GosiRateItem>("/api/policies/payroll/gosi-rates", leId),
  listGlMappings: (leId: string) =>
    _listEndpoint<GlMappingItem>("/api/policies/payroll/gl-mapping", leId),
  listBankTemplates: (leId: string) =>
    _listEndpoint<BankTemplateItem>("/api/policies/payroll/bank-templates", leId),
  listTaxWithholding: (leId: string) =>
    _listEndpoint<TaxWithholdingItem>("/api/policies/payroll/tax-withholding", leId),
  // Travel Policies
  listTravelCategories: (leId: string) =>
    _listEndpoint<TravelCategoryItem>("/api/policies/travel/categories", leId),
  listDestinations: (leId: string) =>
    _listEndpoint<DestinationItem>("/api/policies/travel/destinations", leId),
  getAdvanceReconciliation: (leId: string) =>
    _safeGet<AdvanceReconciliationItem | { items: [] }>(
      `/api/policies/travel/advance-reconciliation?legal_entity_id=${encodeURIComponent(leId)}`,
      { items: [] } as { items: [] },
    ),
  listBookingClassRules: (leId: string) =>
    _listEndpoint<BookingClassRuleItem>("/api/policies/travel/booking-class-rules", leId),
  // EOS Policies
  listEosCalcRules: (leId: string) =>
    _listEndpoint<EosCalcRuleItem>("/api/policies/eos/calculation-rules", leId),
  listTerminationReasons: (leId: string) =>
    _listEndpoint<TerminationReasonItem>("/api/policies/eos/termination-reasons", leId),
  listNoticePeriodRules: (leId: string) =>
    _listEndpoint<NoticePeriodRuleItem>("/api/policies/eos/notice-period-rules", leId),
  // Mutaba'aa floor check (used by policy-save.ts)
  checkStatutoryFloor: (policy_key: string, attempted_value: number) =>
    _safeGet<MutabaaFloorAssessment>(
      `/api/policies/check-floor?policy_key=${encodeURIComponent(policy_key)}&attempted_value=${attempted_value}`,
      {
        blocked: false,
        article_citation: null,
        article_summary: null,
        attempted_value,
        floor_value: null,
        warning_message: null,
      },
    ),
};
